import type { Dispatch, SetStateAction } from 'react'

import { ShotlistsService } from '@/services/shotlist.service'
import { UploadFormService } from '@/services/uploadForm.service'

import { PLATFORMS_CODES } from '@/constants/languages'
import type { IOption } from '@/types/selectOption.interface'
import type { ILanguage } from '@/types/task.interface'
import type {
	IFetchUploadForm,
	ISelectedPlatform,
	TPlatformValue
} from '@/types/uploadForm.interface'

export const deletePlatform = async (
	value: TPlatformValue,
	taskId: string,
	selectedSource: TPlatformValue | null,
	selectedPlatformList: ISelectedPlatform[],
	setSelectedPlatformList: Dispatch<SetStateAction<ISelectedPlatform[]>>,
	setSelectedPlatform: Dispatch<SetStateAction<TPlatformValue | null>>,
	setSelectedTranscribingLang: Dispatch<SetStateAction<ILanguage | null>>,
	setAvailableSourceLanguages: Dispatch<SetStateAction<IOption[]>>
) => {
	const shotlist = await ShotlistsService.getShotlist(taskId)

	if (!shotlist) return

	const languagesArray = [shotlist.original, ...shotlist.translations]

	const targetTranslation = languagesArray.find(
		lang => lang?.language === PLATFORMS_CODES[value]
	)

	const translationId = targetTranslation?.id || null

	// If we have translation or original with this ID, we delete it from Shotlist
	if (translationId) {
		await ShotlistsService.deleteShotlistTranslation(taskId, translationId)

		// If transcribing language is not equal to any of platforms languages,
		// we need to delete it with the last platform
		if (languagesArray.length === 2 && selectedPlatformList.length === 1) {
			const originalId = shotlist.original.id
			await ShotlistsService.deleteShotlistTranslation(taskId, originalId)
		}
	}

	// Deleting information about this platform out of the Form
	const currentForm: IFetchUploadForm = await UploadFormService.getFormData(
		taskId
	).catch(() => null)

	const prevOrder = currentForm?.platforms_order || []
	const nextOrder = prevOrder.filter(prev => prev !== value)

	const prevPlatforms = currentForm?.platforms || {}
	const nextPlatforms = { ...prevPlatforms }
	delete nextPlatforms[value]

	const hasPlatforms =
		currentForm?.platforms &&
		typeof currentForm.platforms === 'object' &&
		Object.keys(nextPlatforms).length > 0

	await UploadFormService.deleteFormData(taskId)

	await UploadFormService.sendFormData(taskId, {
		is_completed: false,
		platforms_order: nextOrder,
		...(hasPlatforms ? { platforms: nextPlatforms } : {})
	} as IFetchUploadForm)

	//If we don't have translation or original with this ID, we just clean all the states
	setSelectedPlatformList(prev => {
		const next = prev.filter(p => p.value !== value)

		if (selectedSource === value) {
			setSelectedPlatform(next.length ? next[0].value : null)
		}

		// If we have only one platfrom in selectedPlatformList after deleting, we need to set its autoFilling: false!
		if (next.length === 1 && next[0].autoFilling) {
			return [
				{
					...next[0],
					autoFilling: false
				}
			]
		}

		// If we deleting the First platform we need to clean selectedTranscribingLang and availableSourceLanguages

		if (next.length === 0) {
			setSelectedTranscribingLang(null)
		}

		return next
	})

	// If the First platform language is not equal selectedTranscribingLang
	// we just need to clean availableSourceLanguages
	setAvailableSourceLanguages(prev =>
		prev.filter(lang => lang.value !== PLATFORMS_CODES[value])
	)
}
